import React, { createContext, useContext, useState } from 'react'

// Define the structure for an upload item
export type UploadItem = {
  id: string
  filename: string
  status: 'uploading' | 'success' | 'error' | 'ingesting'
  error?: string
  // Whether the file has been attached to a entity (already uploaded to org)
  attached?: boolean
}

type UploadStatusContextType = {
  uploads: Record<string, UploadItem>
  addUpload: (id: string, filename: string) => void
  updateUpload: (id: string, updates: Partial<UploadItem>) => void
  removeUpload: (id: string) => void
  clearCompleted: () => void
  hasActiveUploads: boolean
}

const UploadStatusContext = createContext<UploadStatusContextType | undefined>(
  undefined
)

export const UploadStatusProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [uploads, setUploads] = useState<Record<string, UploadItem>>({})

  // Calculate if there are any active uploads
  const hasActiveUploads = Object.values(uploads).some(
    (upload) => upload.status === 'uploading' || upload.status === 'ingesting'
  )

  // Add a new upload to the tracking state
  const addUpload = (id: string, filename: string) => {
    setUploads((prev) => ({
      [id]: {
        id,
        filename,
        status: 'uploading',
      },
      ...prev,
    }))
  }

  // Update an existing upload's status
  const updateUpload = (id: string, updates: Partial<UploadItem>) => {
    setUploads((prev) => {
      if (!prev[id]) return prev

      return {
        ...prev,
        [id]: {
          ...prev[id],
          ...updates,
        },
      }
    })
  }

  // Remove an upload from tracking
  const removeUpload = (id: string) => {
    setUploads((prev) => {
      const newUploads = { ...prev }
      delete newUploads[id]
      return newUploads
    })
  }

  // Clear all completed uploads (success or error)
  const clearCompleted = () => {
    setUploads((prev) => {
      const newUploads = { ...prev }
      Object.keys(newUploads).forEach((id) => {
        if (
          newUploads[id].status === 'success' ||
          newUploads[id].status === 'error'
        ) {
          delete newUploads[id]
        }
      })
      return newUploads
    })
  }

  return (
    <UploadStatusContext.Provider
      value={{
        uploads,
        addUpload,
        updateUpload,
        removeUpload,
        clearCompleted,
        hasActiveUploads,
      }}
    >
      {children}
    </UploadStatusContext.Provider>
  )
}

export const useUploadStatus = () => {
  const context = useContext(UploadStatusContext)
  if (context === undefined) {
    throw new Error(
      'useUploadStatus must be used within an UploadStatusProvider'
    )
  }
  return context
}
